import React from 'react';

import intersection from 'lodash/intersection';
import Option from 'react-select/lib/components/Option';

import {SweetReactSelect} from 'app/components/general/SweetReactSelect';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';
import {capitalizeFirstLetter} from 'app/utils/strings';

import type {contentType} from 'app/utils/content-type';
import type {OptionProps} from 'react-select/lib/components/Option';

export type OptionValueType = {
  label: string;
  value: string;
  description?: string;
  incompatible?: Array<string>;
};

export const getOptions = (ct: contentType): Array<OptionValueType> => {
  const ctName = getContentType(ct).pluralName;

  return [
    {
      label: gt('My %s', ctName),
      value: 'mine',
      description: capitalizeFirstLetter(
        gt('%s that I have contributed to', ctName)
      ),
    },
    {
      label: gt('Needs Approval'),
      value: 'needs_approval',
      description: capitalizeFirstLetter(
        gt('%s you manage with pending approval requested', ctName)
      ),
    },
    {
      label: gt('Draft Only'),
      value: 'is_draft',
      description: capitalizeFirstLetter(
        gt('%s that have never been approved', ctName)
      ),
    },
    {
      label: gt('Pending Draft'),
      value: 'is_pending_draft',
      description: capitalizeFirstLetter(
        gt('%s with recent unapproved drafts', ctName)
      ),
    },
    {
      label: gt('Needs Signing'),
      value: 'signoff',
      description: capitalizeFirstLetter(
        gt('%s which you were requested to sign', ctName)
      ),
    },
    {
      label: gt('Needs Review'),
      value: 'needs_review',
      description: capitalizeFirstLetter(
        gt('%s with a close or expired review date', ctName)
      ),
      incompatible: ['disabled_review'],
    },
    {
      label: gt('Reviews Disabled'),
      value: 'disabled_review',
      description: capitalizeFirstLetter(
        gt('%s you manage without reviews enabled', ctName)
      ),
      incompatible: ['needs_review'],
    },
    {
      label: gt('Reviewer Unassigned'),
      value: 'reviewer_unassigned',
      description: capitalizeFirstLetter(
        gt(
          '%s you manage with reviews enabled that do not have a reviewer',
          ctName
        )
      ),
      incompatible: ['disabled_review'],
    },

    {
      label: gt('Public'),
      value: 'public',
      description: gt('Public %s only', ctName),
      incompatible: ['private'],
    },
    {
      label: gt('Private'),
      value: 'private',
      description: gt('Private %s only', ctName),
      incompatible: ['public'],
    },
    {
      label: gt('In a Knowledge Base'),
      value: 'is_in_knowledgebase',
      description: capitalizeFirstLetter(
        gt('%s that are added to any of your knowledge bases', ctName)
      ),
      incompatible: ['is_not_in_knowledgebase'],
    },
    {
      label: gt('Not in a Knowledge Base'),
      value: 'is_not_in_knowledgebase',
      description: capitalizeFirstLetter(
        gt('%s that are not added to any of your knowledge bases yet', ctName)
      ),
      incompatible: ['is_in_knowledgebase'],
    },
  ];
};

const getCompatibleOptions = (
  selected: Array<string>,
  ct: contentType,
  hideFilters: Array<string>
): Array<OptionValueType> =>
  getOptions(ct).filter(
    (option) =>
      !hideFilters.includes(option.value) &&
      intersection(option.incompatible || [], selected).length === 0
  );

type Props = {
  ct: contentType;
  filters: Array<string>;
  onChange: (optionValue: Array<string>) => void;
  hideFilters: Array<string>;
};

export function DashboardFilterbarState({
  ct,
  filters,
  onChange,
  hideFilters = [],
}: Props) {
  const availableOptions = getCompatibleOptions(filters, ct, hideFilters);

  return (
    <SweetReactSelect
      isMulti={true}
      className="flex-grow-1"
      onChange={(selected) =>
        onChange(selected != null ? selected.map(({value}) => value) : [])
      }
      maxMenuHeight={380}
      components={{
        Option: DashboardFilterbarStateOption,
      }}
      options={availableOptions}
      placeholder={gt('Filter...')}
      value={availableOptions.filter(({value}) => filters.indexOf(value) > -1)}
    />
  );
}

export const DashboardFilterbarStateOption = ({
  data: {label, description},
  ...other
}: OptionProps) => (
  <Option {...other}>
    {label}
    {description != null ? <div className="small">{description}</div> : null}
  </Option>
);
